import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { useState, useMemo } from "react"
import { api } from "../../../../convex/_generated/api"
import type { Id, Doc } from "../../../../convex/_generated/dataModel"
import { useWildGuessSoloEnabled } from "@/lib/analytics/posthog"

export const Route = createFileRoute("/_authed/admin/content")({
  component: AdminContent,
})

function AdminContent() {
  const soloEnabled = useWildGuessSoloEnabled()

  if (!soloEnabled) {
    return (
      <div className="rounded-md border border-dashed border-[color:var(--border)] bg-[color:var(--muted)]/30 p-8 text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[color:var(--muted-foreground)]">
          Feature hidden
        </p>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight">
          Wild Guess Solo is behind a flag.
        </h1>
        <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-[color:var(--muted-foreground)]">
          Content tools for daily questions and solo schedules are hidden while
          the console stays focused on Lyrically Challenged.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Content</h1>
        <p className="mt-1 text-sm text-[color:var(--muted-foreground)]">
          Question library + daily/weekly schedule. All changes audit-logged.
        </p>
      </header>

      <StatsBar />
      <BulkImportSection />
      <AddQuestionSection />
      <DailyScheduleSection />
      <WeeklyDropSection />
      <LibraryTable />
    </div>
  )
}

function StatsBar() {
  const stats = useQuery(api.questions.stats)
  if (!stats) return null
  return (
    <div className="grid gap-3 sm:grid-cols-4">
      <StatTile label="Library size" value={stats.total} />
      <StatTile label="Evergreen pool" value={stats.evergreen} />
      <StatTile label="Used in last 90d (solo)" value={stats.recentlyUsedSolo} />
      <StatTile label="Never used" value={stats.neverUsed} />
    </div>
  )
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-[color:var(--border)] p-3">
      <p className="text-[10px] uppercase tracking-wide text-[color:var(--muted-foreground)]">
        {label}
      </p>
      <p className="mt-1 font-mono text-2xl font-semibold tabular-nums">
        {value}
      </p>
    </div>
  )
}

function BulkImportSection() {
  const bulkImport = useMutation(api.questions.bulkImport)
  const [text, setText] = useState("")
  const [status, setStatus] = useState<
    { state: "idle" } | { state: "running" } | { state: "ok"; count: number } | { state: "error"; message: string }
  >({ state: "idle" })

  async function handleImport() {
    setStatus({ state: "running" })
    try {
      const parsed = JSON.parse(text)
      if (!Array.isArray(parsed)) throw new Error("Expected a JSON array")
      const result = await bulkImport({ questions: parsed })
      setStatus({ state: "ok", count: result.count })
      setText("")
    } catch (err) {
      setStatus({ state: "error", message: (err as Error).message })
    }
  }

  return (
    <section className="rounded-md border border-[color:var(--border)] p-4">
      <h2 className="text-sm font-semibold">Bulk import (JSON)</h2>
      <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
        Paste a JSON array of objects shaped like{" "}
        <code className="font-mono">
          {`{prompt, choices: [], correctIndex, category?, decade?, explanation?, isEvergreen?}`}
        </code>
        . Dedup against the Google Drive source before pasting.
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        aria-label="Bulk question import JSON"
        placeholder='[{"prompt": "...", "choices": ["A","B","C","D"], "correctIndex": 0}]'
        rows={6}
        className="mt-3 w-full rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-3 py-2 font-mono text-xs"
      />
      <div className="mt-2 flex items-center gap-2">
        <button
          onClick={handleImport}
          disabled={!text || status.state === "running"}
          className="rounded-md bg-[color:var(--primary)] px-3 py-1.5 text-xs font-medium text-[color:var(--primary-foreground)] disabled:opacity-50"
        >
          {status.state === "running" ? "Importing…" : "Import"}
        </button>
        {status.state === "ok" && (
          <span className="text-xs text-green-700 dark:text-green-400">
            Imported {status.count} questions.
          </span>
        )}
        {status.state === "error" && (
          <span className="text-xs text-red-600">{status.message}</span>
        )}
      </div>
    </section>
  )
}

function AddQuestionSection() {
  const create = useMutation(api.questions.create)
  const [open, setOpen] = useState(false)
  const [prompt, setPrompt] = useState("")
  const [choices, setChoices] = useState(["", "", "", ""])
  const [correctIndex, setCorrectIndex] = useState(0)
  const [category, setCategory] = useState("")
  const [decade, setDecade] = useState("")
  const [explanation, setExplanation] = useState("")
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    try {
      await create({
        prompt,
        choices: choices.filter((c) => c.trim()),
        correctIndex,
        category: category || undefined,
        decade: decade || undefined,
        explanation: explanation || undefined,
      })
      setPrompt("")
      setChoices(["", "", "", ""])
      setCorrectIndex(0)
      setCategory("")
      setDecade("")
      setExplanation("")
      setOpen(false)
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="rounded-md border border-dashed border-[color:var(--border)] px-3 py-1.5 text-xs font-medium hover:bg-[color:var(--muted)]"
      >
        + Add a single question
      </button>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-md border border-[color:var(--border)] p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">New question</h2>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-xs text-[color:var(--muted-foreground)]"
        >
          Cancel
        </button>
      </div>
      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        aria-label="Question prompt"
        placeholder="Prompt"
        rows={2}
        required
        className="w-full rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-3 py-2 text-sm"
      />
      {choices.map((c, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="radio"
            name="correct"
            checked={correctIndex === i}
            onChange={() => setCorrectIndex(i)}
            aria-label={`Mark choice ${i + 1} as correct`}
          />
          <input
            type="text"
            value={c}
            onChange={(e) => {
              const next = [...choices]
              next[i] = e.target.value
              setChoices(next)
            }}
            aria-label={`Choice ${i + 1}`}
            placeholder={`Choice ${i + 1}`}
            className="flex-1 rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-3 py-2 text-sm"
          />
        </div>
      ))}
      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          aria-label="Question category"
          placeholder="Category (optional)"
          className="rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-3 py-2 text-sm"
        />
        <input
          type="text"
          value={decade}
          onChange={(e) => setDecade(e.target.value)}
          aria-label="Question decade"
          placeholder="Decade (60s, 70s, ...)"
          className="rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-3 py-2 text-sm"
        />
      </div>
      <textarea
        value={explanation}
        onChange={(e) => setExplanation(e.target.value)}
        aria-label="Answer explanation"
        placeholder="Explanation (optional, shown after answer)"
        rows={2}
        className="w-full rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-3 py-2 text-sm"
      />
      <button
        type="submit"
        disabled={submitting || !prompt.trim()}
        className="rounded-md bg-[color:var(--primary)] px-3 py-1.5 text-xs font-medium text-[color:var(--primary-foreground)] disabled:opacity-50"
      >
        {submitting ? "Adding…" : "Add question"}
      </button>
    </form>
  )
}

function ctTodayKey(): string {
  const d = new Date()
  const localOffset = d.getTimezoneOffset() // minutes; positive = behind UTC
  void localOffset
  // Use the same logic as the Convex helper: approximate CT via UTC -6/-5.
  // For the UI we display the client-local "today" — server validates.
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

function ctWeekKey(): string {
  const todayKey = ctTodayKey()
  const [y, m, d] = todayKey.split("-").map(Number)
  const date = new Date(Date.UTC(y, m - 1, d))
  const dow = date.getUTCDay()
  const daysSinceMonday = (dow + 6) % 7
  date.setUTCDate(date.getUTCDate() - daysSinceMonday)
  const yy = date.getUTCFullYear()
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0")
  const dd = String(date.getUTCDate()).padStart(2, "0")
  return `${yy}-${mm}-${dd}`
}

function addDaysKey(dateKey: string, days: number): string {
  const [y, m, d] = dateKey.split("-").map(Number)
  const date = new Date(Date.UTC(y, m - 1, d))
  date.setUTCDate(date.getUTCDate() + days)
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-")
}

function DailyScheduleSection() {
  const assign = useMutation(api.questionSchedule.assignDaily)
  const remove = useMutation(api.questionSchedule.removeSlot)
  const today = ctTodayKey()
  const fromKey = today
  const toKey = addDaysKey(today, 13)
  const schedule = useQuery(api.questionSchedule.listForRange, {
    slotType: "daily",
    fromDateKey: fromKey,
    toDateKey: toKey,
  })
  const library = useQuery(api.questions.list, { limit: 500 })
  const [selectedQuestionByDate, setSelectedQuestionByDate] = useState<
    Record<string, Id<"questions">>
  >({})

  const dates: string[] = useMemo(() => {
    const out: string[] = []
    for (let i = 0; i < 14; i++) out.push(addDaysKey(today, i))
    return out
  }, [today])

  if (!schedule || !library) {
    return (
      <section className="rounded-md border border-[color:var(--border)] p-4">
        <h2 className="text-sm font-semibold">Daily schedule</h2>
        <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
          Loading…
        </p>
      </section>
    )
  }

  const byDate = new Map(schedule.map((row) => [row.dateKey, row]))

  return (
    <section className="rounded-md border border-[color:var(--border)] p-4">
      <h2 className="text-sm font-semibold">Daily schedule</h2>
      <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
        Next 14 days. Assign a question per day; auto-pull fallback fires if a
        slot is empty by midnight CT.
      </p>
      <div className="mt-3 space-y-2">
        {dates.map((dateKey) => {
          const row = byDate.get(dateKey)
          const assigned = row?.question as Doc<"questions"> | null | undefined
          return (
            <div
              key={dateKey}
              className="flex items-center gap-2 rounded-md border border-[color:var(--border)] px-3 py-2"
            >
              <span className="font-mono text-xs tabular-nums w-24">
                {dateKey}
              </span>
              {assigned ? (
                <>
                  <span className="flex-1 text-xs">{assigned.prompt}</span>
                  <button
                    onClick={() => row && remove({ scheduleId: row._id })}
                    className="rounded-md border border-[color:var(--border)] px-2 py-1 text-[10px] hover:bg-[color:var(--muted)]"
                  >
                    Clear
                  </button>
                </>
              ) : (
                <>
                  <select
                    value={selectedQuestionByDate[dateKey] ?? ""}
                    onChange={(e) =>
                      setSelectedQuestionByDate({
                        ...selectedQuestionByDate,
                        [dateKey]: e.target.value as Id<"questions">,
                      })
                    }
                    className="flex-1 rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-2 py-1 text-xs"
                  >
                    <option value="">Select a question…</option>
                    {library.map((q) => (
                      <option key={q._id} value={q._id}>
                        {q.prompt.slice(0, 80)}
                      </option>
                    ))}
                  </select>
                  <button
                    disabled={!selectedQuestionByDate[dateKey]}
                    onClick={async () => {
                      const qid = selectedQuestionByDate[dateKey]
                      if (!qid) return
                      await assign({ dateKey, questionId: qid })
                      setSelectedQuestionByDate({
                        ...selectedQuestionByDate,
                        [dateKey]: "" as Id<"questions">,
                      })
                    }}
                    className="rounded-md bg-[color:var(--primary)] px-2 py-1 text-[10px] font-medium text-[color:var(--primary-foreground)] disabled:opacity-50"
                  >
                    Assign
                  </button>
                </>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}

function WeeklyDropSection() {
  const assign = useMutation(api.questionSchedule.assignWeekly)
  const thisWeek = ctWeekKey()
  const nextWeek = addDaysKey(thisWeek, 7)
  const schedule = useQuery(api.questionSchedule.listForRange, {
    slotType: "weekly",
    fromDateKey: thisWeek,
    toDateKey: addDaysKey(thisWeek, 13),
  })
  const library = useQuery(api.questions.list, { limit: 500 })
  const [activeWeek, setActiveWeek] = useState(thisWeek)
  const [picks, setPicks] = useState<Id<"questions">[]>([])

  if (!schedule || !library) {
    return (
      <section className="rounded-md border border-[color:var(--border)] p-4">
        <h2 className="text-sm font-semibold">Weekly drop</h2>
        <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
          Loading…
        </p>
      </section>
    )
  }

  const currentSlots = schedule
    .filter((s) => s.dateKey === activeWeek)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))

  async function handleSave() {
    await assign({ weekKey: activeWeek, questionIds: picks })
    setPicks([])
  }

  return (
    <section className="rounded-md border border-[color:var(--border)] p-4">
      <h2 className="text-sm font-semibold">Weekly drop</h2>
      <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
        5–10 questions, released Monday morning. Switching weeks shows the
        existing assignment.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <button
          onClick={() => setActiveWeek(thisWeek)}
          className={[
            "rounded-md border px-2 py-1 text-xs",
            activeWeek === thisWeek
              ? "border-[color:var(--primary)] bg-[color:var(--primary)]/10"
              : "border-[color:var(--border)]",
          ].join(" ")}
        >
          This week ({thisWeek})
        </button>
        <button
          onClick={() => setActiveWeek(nextWeek)}
          className={[
            "rounded-md border px-2 py-1 text-xs",
            activeWeek === nextWeek
              ? "border-[color:var(--primary)] bg-[color:var(--primary)]/10"
              : "border-[color:var(--border)]",
          ].join(" ")}
        >
          Next week ({nextWeek})
        </button>
      </div>

      {currentSlots.length > 0 && (
        <div className="mt-3 space-y-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[color:var(--muted-foreground)]">
            Current drop
          </p>
          {currentSlots.map((slot, i) => {
            const q = slot.question as Doc<"questions"> | null
            return (
              <div
                key={slot._id}
                className="flex items-center gap-2 text-xs px-2 py-1"
              >
                <span className="font-mono w-4 text-[color:var(--muted-foreground)]">
                  {i + 1}
                </span>
                <span className="flex-1">
                  {q?.prompt ?? "(question deleted)"}
                </span>
              </div>
            )
          })}
        </div>
      )}

      <div className="mt-4 space-y-1">
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[color:var(--muted-foreground)]">
          Build new drop ({picks.length} picked)
        </p>
        <div className="max-h-64 overflow-y-auto rounded-md border border-[color:var(--border)] divide-y divide-[color:var(--border)]">
          {library.map((q) => {
            const picked = picks.includes(q._id)
            return (
              <button
                key={q._id}
                onClick={() => {
                  if (picked) setPicks(picks.filter((id) => id !== q._id))
                  else if (picks.length < 10) setPicks([...picks, q._id])
                }}
                className={[
                  "w-full px-3 py-1.5 text-left text-xs flex items-center gap-2",
                  picked
                    ? "bg-[color:var(--primary)]/10"
                    : "hover:bg-[color:var(--muted)]",
                ].join(" ")}
              >
                <span
                  className={[
                    "inline-flex h-4 w-4 items-center justify-center rounded-full font-mono text-[9px]",
                    picked
                      ? "bg-[color:var(--primary)] text-white"
                      : "border border-[color:var(--border)]",
                  ].join(" ")}
                >
                  {picked ? picks.indexOf(q._id) + 1 : ""}
                </span>
                <span className="flex-1 truncate">{q.prompt}</span>
              </button>
            )
          })}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={picks.length < 1 || picks.length > 10}
            className="rounded-md bg-[color:var(--primary)] px-3 py-1.5 text-xs font-medium text-[color:var(--primary-foreground)] disabled:opacity-50"
          >
            Save as {activeWeek} drop
          </button>
          {picks.length > 0 && (
            <button
              onClick={() => setPicks([])}
              className="rounded-md border border-[color:var(--border)] px-3 py-1.5 text-xs hover:bg-[color:var(--muted)]"
            >
              Clear picks
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

function LibraryTable() {
  const remove = useMutation(api.questions.remove)
  const library = useQuery(api.questions.list, { limit: 500 })
  const [q, setQ] = useState("")
  if (!library) {
    return (
      <section className="rounded-md border border-[color:var(--border)] p-4">
        <h2 className="text-sm font-semibold">Library</h2>
        <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
          Loading…
        </p>
      </section>
    )
  }
  const filtered = q
    ? library.filter(
        (row) =>
          row.prompt.toLowerCase().includes(q.toLowerCase()) ||
          (row.category ?? "").toLowerCase().includes(q.toLowerCase()) ||
          (row.decade ?? "").toLowerCase().includes(q.toLowerCase()),
      )
    : library
  return (
    <section className="rounded-md border border-[color:var(--border)] p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">
          Library ({filtered.length} of {library.length})
        </h2>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search question library"
          placeholder="Search prompt, category, decade…"
          className="w-64 rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-2 py-1 text-xs"
        />
      </div>
      <div className="mt-3 max-h-96 overflow-y-auto rounded-md border border-[color:var(--border)] divide-y divide-[color:var(--border)]">
        {filtered.map((row) => (
          <div
            key={row._id}
            className="flex items-center gap-2 px-3 py-2 text-xs"
          >
            <span className="flex-1">{row.prompt}</span>
            {row.category && (
              <span className="font-mono text-[10px] text-[color:var(--muted-foreground)]">
                {row.category}
              </span>
            )}
            {row.decade && (
              <span className="font-mono text-[10px] text-[color:var(--muted-foreground)]">
                {row.decade}
              </span>
            )}
            {row.isEvergreen && (
              <span className="rounded bg-[color:var(--accent)]/40 px-1.5 py-0.5 text-[9px] uppercase tracking-wide">
                evergreen
              </span>
            )}
            <button
              onClick={() => {
                if (confirm("Delete this question?")) remove({ id: row._id })
              }}
              className="text-[10px] text-red-600"
            >
              Delete
            </button>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="px-3 py-6 text-center text-xs text-[color:var(--muted-foreground)]">
            No questions match.
          </p>
        )}
      </div>
    </section>
  )
}
